import {
  AxisType,
  Channel,
  ChannelSuggestion,
  ComboBox,
  ComboBoxOption,
  reorderChannels,
  type EventLabelPlacement,
  suggestChannels,
} from '@lmt/signal-viewer-ui';
import { makeId } from '../../utils/profiler-helper';
import React, { useMemo, useState } from 'react';
import { PlusIcon } from '../icons/PlusIcon';
import { TrashIcon } from '../icons/TrashIcon';
import { DragIcon } from '../icons/DragIcon';
import './ChannelsEditor.css';

export type ChannelsEditorProps = {
  config: Channel[];
  availableSignalTypes: string[];
  onChange: (next: Channel[]) => void;
  onAddSuggestedChannel?: (suggestion: ChannelSuggestion) => void;
};

const createEmptySignal = (type: string) => ({
  type,
  config: {
    label: 'New signal',
    color: '#222d2c',
  },
});

const createEmptyChannel = (): Channel => ({
  id: makeId(),
  configuration: {
    name: 'custom-' + Date.now(),
    relativeHeight: 18,
    tickInterval: { start: 0, end: 25, step: 5 },
    axisType: AxisType.SignalsWithEvents,
  },
  signals: [],
  events: [],
});

const toOptions = (values: string[]): ComboBoxOption[] =>
  Array.from(new Set(values))
    .filter(Boolean)
    .map((value) => ({
      value,
      label: value,
    }));

const eventLabelPlacementOptions: ComboBoxOption[] = [
  { value: 'top-left', label: 'Top left' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'center', label: 'Center' },
  { value: 'none', label: 'No label' },
];

export const ChannelsEditor: React.FC<ChannelsEditorProps> = ({
  config,
  availableSignalTypes,
  onChange,
  onAddSuggestedChannel,
}) => {
  const [draggedChannelId, setDraggedChannelId] = useState<string | null>(null);
  const [dropTargetChannelId, setDropTargetChannelId] = useState<string | null>(
    null,
  );

  const suggestions = useMemo(
    () => suggestChannels(availableSignalTypes),
    [availableSignalTypes],
  );

  const handleChannelNameChange = (channelIdx: number, newName: string) => {
    const next = structuredClone(config);
    next[channelIdx].configuration.name = newName;
    onChange(next);
  };

  const handleSignalTypeChange = (
    channelIdx: number,
    signalIdx: number,
    newType: string,
  ) => {
    const next = structuredClone(config);
    next[channelIdx].signals[signalIdx].type = newType;
    onChange(next);
  };

  const handleSignalLabelChange = (
    channelIdx: number,
    signalIdx: number,
    newLabel: string,
  ) => {
    const next = structuredClone(config);
    next[channelIdx].signals[signalIdx].config.label = newLabel;
    onChange(next);
  };

  const handleSignalColorChange = (
    channelIdx: number,
    signalIdx: number,
    newColor: string,
  ) => {
    const next = structuredClone(config);
    next[channelIdx].signals[signalIdx].config.color = newColor;
    onChange(next);
  };

  const handleAddSignal = (channelIdx: number) => {
    const next = structuredClone(config);
    next[channelIdx].signals.push(
      createEmptySignal(availableSignalTypes[0] ?? 'Unknown'),
    );
    onChange(next);
  };

  const handleRemoveSignal = (channelIdx: number, signalIdx: number) => {
    const next = structuredClone(config);
    next[channelIdx].signals.splice(signalIdx, 1);
    onChange(next);
  };

  const handleEventColorChange = (
    channelIdx: number,
    eventIdx: number,
    newColor: string,
  ) => {
    const next = structuredClone(config);
    next[channelIdx].events[eventIdx].config.fillColor = newColor;
    onChange(next);
  };

  const handleEventLabelPlacementChange = (
    channelIdx: number,
    eventIdx: number,
    labelPlacement: EventLabelPlacement,
  ) => {
    const next = structuredClone(config);
    next[channelIdx].events[eventIdx].config.labelPlacement = labelPlacement;
    onChange(next);
  };

  const handleAddChannel = () => {
    const next = structuredClone(config);
    next.push(createEmptyChannel());
    onChange(next);
  };

  const handleRemoveChannel = (channelIdx: number) => {
    const next = structuredClone(config);
    next.splice(channelIdx, 1);
    onChange(next);
  };

  const handleRemoveAllChannels = () => {
    onChange([]);
  };

  const handleChannelDragStart = (channelId: string) => {
    setDraggedChannelId(channelId);
  };

  const handleChannelDragEnd = () => {
    setDraggedChannelId(null);
    setDropTargetChannelId(null);
  };

  const handleDropOnChannel = (targetChannelId: string) => {
    if (!draggedChannelId) {
      return;
    }

    const next = reorderChannels(config, draggedChannelId, targetChannelId);
    onChange(next);

    setDraggedChannelId(null);
    setDropTargetChannelId(null);
  };

  return (
    <div className='channel-config-section'>
      <div className='channel-config-section-header'>
        <div className='channel-config-section-title'>CHANNELS</div>

        <div className='channel-config-section-actions'>
          <button
            type='button'
            onClick={handleAddChannel}
            className='channel-config-section-primary-button'
          >
            <PlusIcon color={'#FFFFFF'} size={9} />
            Create new channel
          </button>

          <button
            type='button'
            onClick={handleRemoveAllChannels}
            className='channel-config-section-secondary-button'
          >
            <TrashIcon color={'#8B8B8B'} />
            Remove all channels
          </button>
        </div>
      </div>

      <div className='channel-config-available'>
        {suggestions.length > 0 && onAddSuggestedChannel && (
          <div className='channel-config-label'>
            Available channels in file
            <div className='channel-config-suggestions'>
              <div className='channel-config-suggestions-list'>
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.templateId}
                    type='button'
                    className='channel-config-suggestion-button'
                    onClick={() => onAddSuggestedChannel(suggestion)}
                  >
                    {suggestion.title} ({suggestion.score})
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {config.map((channel, channelIdx) => {
        const isDragging = draggedChannelId === channel.id;
        const isDropTarget = dropTargetChannelId === channel.id;

        return (
          <div
            key={channel.id ?? channelIdx}
            className={[
              'channel-config-channel',
              isDragging ? 'channel-config-channel--dragging' : '',
              isDropTarget ? 'channel-config-channel--drop-target' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onDragOver={(e) => {
              e.preventDefault();
              if (dropTargetChannelId !== channel.id) {
                setDropTargetChannelId(channel.id);
              }
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              setDropTargetChannelId(channel.id);
            }}
            onDragLeave={() => {
              if (dropTargetChannelId === channel.id) {
                setDropTargetChannelId(null);
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              handleDropOnChannel(channel.id);
            }}
          >
            <div className='channel-config-channel-header'>
              <div className='channel-config-channel-title'>
                <button
                  type='button'
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', channel.id);
                    handleChannelDragStart(channel.id);
                  }}
                  onDragEnd={handleChannelDragEnd}
                  className='channel-config-channel-drag-button'
                  aria-label={`Reorder ${channel.configuration.name}`}
                >
                  <DragIcon color={'#A3A3A3'} size={36} />
                </button>

                <span className='channel-config-channel-name'>
                  Channel {channelIdx + 1}
                </span>
              </div>

              <div className='channel-config-channel-actions'>
                <button
                  type='button'
                  onClick={() => handleAddSignal(channelIdx)}
                  className='channel-config-action-button'
                >
                  <PlusIcon color={'#003568'} size={9} />
                  Add signal
                </button>

                <div className='channel-config-channel-action-divider' />

                <button
                  type='button'
                  onClick={() => handleRemoveChannel(channelIdx)}
                  className='channel-config-action-button'
                >
                  <TrashIcon color={'#8B8B8B'} />
                  Remove channel
                </button>
              </div>
            </div>

            <input
              type='text'
              value={channel.configuration.name}
              onChange={(e) =>
                handleChannelNameChange(channelIdx, e.target.value)
              }
              placeholder='Field text'
              className='channel-config-input'
            />

            {channel.signals.length > 0 && (
              <div className='channel-config-signals-label'>Signals:</div>
            )}

            <div className='channel-config-signals-list'>
              {channel.signals.map((signal, signalIdx) => {
                const currentSignalTypeOptions = toOptions(
                  availableSignalTypes.length > 0
                    ? availableSignalTypes
                    : [signal.type || 'Unknown'],
                );

                return (
                  <div
                    key={`${channel.id ?? channelIdx}-${signalIdx}`}
                    className='channel-config-signal-row'
                  >
                    <ComboBox
                      value={signal.type}
                      options={currentSignalTypeOptions}
                      onChange={(nextValue) =>
                        handleSignalTypeChange(channelIdx, signalIdx, nextValue)
                      }
                      className='channel-config-combobox'
                    />

                    <input
                      value={signal.config.label}
                      onChange={(e) =>
                        handleSignalLabelChange(
                          channelIdx,
                          signalIdx,
                          e.target.value,
                        )
                      }
                      placeholder='Field text'
                      className='channel-config-input'
                    />

                    <div className='channel-config-color-wrap'>
                      <input
                        type='color'
                        value={signal.config.color}
                        onChange={(e) =>
                          handleSignalColorChange(
                            channelIdx,
                            signalIdx,
                            e.target.value,
                          )
                        }
                        className='channel-config-color-input'
                      />
                    </div>

                    <button
                      type='button'
                      onClick={() => handleRemoveSignal(channelIdx, signalIdx)}
                      className='channel-config-remove-button'
                      aria-label='Remove signal'
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
            </div>

            {channel.events.length > 0 && (
              <>
                <div className='channel-config-signals-label'>Events:</div>

                <div className='channel-config-signals-list'>
                  {channel.events.map((event, eventIdx) => (
                    <div
                      key={`${channel.id ?? channelIdx}-event-${eventIdx}`}
                      className='channel-config-event-row'
                    >
                      <div className='channel-config-event-surface'>
                        <span className='channel-config-event-type'>Event</span>
                        <span className='channel-config-event-label'>
                          {event.config.label}
                        </span>
                      </div>

                      <ComboBox
                        value={event.config.labelPlacement ?? 'center'}
                        options={eventLabelPlacementOptions}
                        onChange={(nextValue) =>
                          handleEventLabelPlacementChange(
                            channelIdx,
                            eventIdx,
                            nextValue as EventLabelPlacement,
                          )
                        }
                        className='channel-config-combobox channel-config-event-label-placement-combobox'
                      />

                      <div className='channel-config-color-wrap'>
                        <input
                          type='color'
                          value={event.config.fillColor}
                          onChange={(e) =>
                            handleEventColorChange(
                              channelIdx,
                              eventIdx,
                              e.target.value,
                            )
                          }
                          className='channel-config-color-input'
                        />
                      </div>

                      <div className='channel-config-event-spacer' aria-hidden />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};
